/**
 * Click & Win entry flow — a custom element wrapping sections/click-and-
 * win.liquid. This file owns everything that section can't know at render
 * time: which of the N giveaway panels is visible, and everything the
 * engagement Lambda's summary/entries endpoints report — round number,
 * per-giveaway entered/complete state, and the running daily entry total.
 *
 * BACKEND CONTRACT: CLIENT_DEV_GUIDE.md → "Click & Win" in the engagement
 * Lambda repo is authoritative; docs/superpowers/specs/2026-09-17-click-and-
 * win-backend-wiring-design.md records this rewrite's design (and predates
 * the card model — see that file's own superseded notes). Summary of the
 * rule that governs everything below: the page only renders what the server
 * says. It never decides eligibility, round number, or entry amounts —
 * #summary (from the engagement Lambda's summary endpoint) is the only
 * source for all of that, cached in-memory for the page session and never
 * persisted.
 *
 * NAVIGATION: the section can't read `?step=` server-side (theme Liquid has
 * no generic query-string access), so every giveaway panel + the done panel
 * are still rendered into one page load, and this file's job on connect is
 * only to show the one panel that matches `?step=` in `location.search`.
 * Moving BETWEEN steps, though, is a REAL page load — Prev/Next
 * (snippets/click-and-win-prize-card.liquid) are plain `<a href="?step=">`
 * links with no JS involved, and every JS-driven advance (Enter's
 * post-confirm "Next"/auto-advance, "start next round", the daily-cap
 * auto-advance) sets `location.href` rather than `history.pushState`. Each
 * step gets its own ad auction this way, and a real load re-fetches the
 * summary from the server instead of trusting an in-memory copy. The one
 * exception is the silent "already complete on load" redirect in
 * #applySummary, which repaints in place via `history.replaceState` instead
 * of a second page load, since it corrects a state the member never
 * navigated to and never asked to see.
 *
 * ENTER FLOW: a successful or duplicate Enter shows a "confirmed" overlay
 * on the SAME panel (Figma node 7065:6374, "PCH Interstitial" — the Enter
 * button relabels to "Next" and stays clickable, the prize image gets a
 * brand-orange overlay) for ENTER_CONFIRM_DURATION_MS, then auto-advances
 * to the next step — unless the member leaves the panel some other way
 * first, which cancels the pending auto-advance via #applyStep()'s
 * #confirmTimeout guard, see that method's own comment. ONE Enter enters
 * ONE giveaway and moves ONE step — to that panel's own `data-next-target`
 * — EXCEPT when that Enter is the one that finishes the round (or the day):
 * then it lands on the done panel instead, whichever panel it was made
 * from. Decided by #afterEntryTarget from the model's own counts, never
 * from the response body — see #handleEntryResponse's comment for why the
 * server's round_completed is still not routed on.
 * There is deliberately no "+N entries" number anywhere in this flow (the
 * prize card never had a slot for it, and adding one was explicitly
 * descoped) — the overlay's copy is a static, generic confirmation.
 *
 * WHAT THIS FILE DOES NOT DO: it never re-fetches the summary immediately
 * after an Enter POST. The POST response's own count
 * (entries_today_for_giveaway) is authoritative for that one click, and every
 * round-level value is re-derived from the counts (#deriveRoundState); the one
 * case where the page can be running on stale round info (a second tab
 * already completed this round — 422 ROUND_NOT_COMPLETE) is corrected on
 * the next real page load, not with an extra round-trip here.
 *
 * NO-SESSION RENDER (logged-out visitors + theme editor): the section no
 * longer hides the page behind a login wall — see sections/click-and-
 * win.liquid's "NO-SESSION RENDER" header note. When the flow element's
 * `data-logged-in` is not "true", or the page is in Shopify.designMode,
 * connectedCallback picks the visible panel, paints the round-1 eyebrow
 * (#renderNoSessionPanels) and STOPS: no context fetch, no summary fetch,
 * nothing that could 401 and trip the reload-on-401 handling below. The
 * prize card snippet renders a login link in the Enter slot for that case,
 * so there is no `.caw__btn--enter` for anything here to touch. A
 * no-session `?step=done` is redirected to step 1 in place — the done
 * panel is entirely summary-driven and has nothing to show a visitor.
 * Prev/Next keep working (plain links), so a visitor can browse the deck.
 *
 * SUMO: module 'clickandwin' (flat, matching watch-and-win-v5.js's own
 * 'watchwin' — the Lambda's "engagement/clickandwin" logging namespace is
 * a server-side concern, not this client field). Every field not in
 * sumo-client.js's whitelist (event_name, module, shopify_customer_id,
 * source_ref, contest_key, game_id, result, error_type, latency_ms,
 * placement) goes under `extra` — see #logEvent.
 */

// App Proxy base path. Set by the section via window.__PCH_APP_PROXY_BASE
// (sourced from the per-theme `app_proxy_base` setting). Falls back to the
// production default if the global isn't set, so the asset stays runnable
// even if loaded outside the click-and-win section context. Same pattern as
// assets/watch-and-win-v5.js's own APP_PROXY_BASE.
const APP_PROXY_BASE = (typeof window.__PCH_APP_PROXY_BASE === 'string' && window.__PCH_APP_PROXY_BASE)
  ? window.__PCH_APP_PROXY_BASE
  : '/apps/engagement';

// How long the post-Enter confirmation overlay stays visible before
// auto-advancing to the next step. A UX default, not a value specified by
// Figma or the backend contract.
const ENTER_CONFIRM_DURATION_MS = 5000;

// Shown on the Enter button when the summary endpoint itself fails
// (non-200, non-410) — every button is disabled with this copy rather than
// falling back to any mocked count. Same copy as the equivalent Enter-POST
// failure in #handleEntryError.
const GENERIC_ERROR_LABEL = 'Something went wrong. Try again.';

// Enter button data-state -> the data-label-* attribute holding that
// state's label, spelled out explicitly so a typo here fails loudly in
// review rather than silently via string concatenation.
const STATE_LABEL_KEYS = {
  available: 'labelAvailable',
  pending: 'labelPending',
  entered: 'labelEntered',
  complete: 'labelComplete',
  confirmed: 'labelConfirmed',
  unconfigured: 'labelUnconfigured',
};

// Figma's ledger table shows entries with thousands separators (e.g. the
// all-rounds-complete grand total, "2,250") — applied uniformly to every
// entries/total value since smaller per-row numbers format identically
// either way.
function formatEntries(n) {
  return n.toLocaleString('en-US');
}

// Figma bolds only the leading word ("ROUND") of the eyebrow, leaving the
// "{1 of 3}" part — which always contains the [[ round ]] placeholder — at
// the surrounding regular weight. Splitting on the first "{" reproduces
// that same bold/regular split for a merchant-customized label without a
// second theme setting.
function renderEyebrowText(el, template, roundValue) {
  const braceIndex = template.indexOf('{');
  const boldPart = (braceIndex === -1 ? template : template.slice(0, braceIndex)).replace('[[ round ]]', String(roundValue));
  const restPart = (braceIndex === -1 ? '' : template.slice(braceIndex)).replace('[[ round ]]', String(roundValue));
  el.textContent = '';
  if (boldPart) {
    const strong = document.createElement('strong');
    strong.textContent = boldPart;
    el.append(strong);
  }
  if (restPart) el.append(restPart);
}

class ClickAndWinFlow extends HTMLElement {
  // cardId (a metaobject id string) identifies the panel and is unique;
  // giveaway keys can repeat across panels, so they're for reporting only.
  /** @type {{index: number, cardId: string, key: string, title: string, nextTarget: string, el: HTMLElement}[]} */
  #deck = [];

  /** @type {HTMLElement | null} */
  #doneEl = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #confirmTimeout = null;

  /** @type {string | null} */
  #pendingCardId = null;

  /** @type {{payload: string, signature: string} | null} */
  #shopContext = null;

  // True only when the page was opened with no ?step= at all (a plain visit
  // to /pages/click-and-win, not a link to a specific round/giveaway or a
  // prev/next-driven step). Set once in connectedCallback, before the
  // summary has loaded, and read once in #applySummary to decide whether an
  // already-all-complete flow should redirect to the done panel — see that
  // check's own comment for why an explicit ?step= is always honored instead.
  #landedOnDefaultStep = false;

  /** @type {object | null} */
  #summary = null;

  /** @type {Map<string, object>} */
  #summaryByCard = new Map();

  // True when there is nobody to fetch for: the flow rendered without a
  // customer session, or the page is the theme editor's preview. Set once
  // in connectedCallback — see the NO-SESSION RENDER header note.
  #noSession = false;

  connectedCallback() {
    this.#deck = [...this.querySelectorAll('.caw__giveaway')].map((el) => ({
      index: Number(el.dataset.giveawayIndex),
      cardId: el.dataset.cardId,
      key: el.dataset.giveawayKey,
      title: el.dataset.giveawayTitle,
      nextTarget: el.dataset.nextTarget,
      el,
    }));
    this.#doneEl = this.querySelector('.caw__done');

    this.addEventListener('click', this.#onClick);

    const rawStep = new URLSearchParams(location.search).get('step');
    this.#landedOnDefaultStep = rawStep === null || rawStep.trim() === '';

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.#noSession = this.dataset.loggedIn !== 'true' || designMode;

    let target = this.#stepFromLocation();
    if (this.#noSession && target === 'done') {
      // The done panel is summary-driven and would render as an empty
      // shell with no session — land on step 1 instead and clean the URL
      // so a reload (or a shared link) doesn't re-request it.
      target = 0;
      const url = new URL(location.href);
      url.searchParams.delete('step');
      history.replaceState(null, '', url);
    }

    // Structural step visibility doesn't depend on the summary — show the
    // right panel immediately, then patch its content once data arrives.
    this.#applyStep(target);

    if (this.#noSession) {
      this.#renderNoSessionPanels();
      return;
    }

    this.#loadContext()
      .then((ctx) => this.#fetchSummary(ctx, false))
      .catch((err) => {
        this.#logEvent('client_error', { error_type: 'context_failed', extra: { message: err && err.message } });
        this.#disableAllEnterButtons(GENERIC_ERROR_LABEL);
      });
  }

  disconnectedCallback() {
    if (this.#confirmTimeout) clearTimeout(this.#confirmTimeout);
  }

  // ---------- Event handlers (arrow class fields so `this` binds and the
  // same reference can be passed to removeEventListener) ----------

  #onClick = (event) => {
    const button = event.target.closest('[data-action]');
    if (!button || !this.contains(button)) return;

    switch (button.dataset.action) {
      case 'enter': {
        // 'confirmed' is the one non-'available' state that stays enabled
        // (relabeled "Next" per Figma's "or click on the next button
        // below") — clicking it advances immediately instead of re-entering.
        if (button.dataset.state === 'confirmed') {
          const giveawayEl = button.closest('.caw__giveaway');
          // afterEntryTarget is stamped by #handleEntryResponse for the Enter
          // that put the button in this state; it and the pending auto-advance
          // MUST agree, or a fast "Next" click skips the round summary.
          if (giveawayEl) this.#navigateTo(this.#resolveTarget(giveawayEl.dataset.afterEntryTarget || giveawayEl.dataset.nextTarget));
          return;
        }
        // Defensive guard: the button should already be disabled for any
        // other non-'available' state, but this protects against stale
        // DOM (e.g. a race with a pending repaint).
        if (button.dataset.state !== 'available') return;
        this.#postEntry(button);
        break;
      }
      case 'start-next-round':
        this.#navigateTo(this.#firstUnenteredIndex());
        break;
      default:
        break;
    }
  };

  // ---------- Navigation (real page loads — see this file's NAVIGATION
  // header note for why) ----------

  // raw is a 0-based digit string (from data-target / data-next-target) or
  // the literal string 'done'.
  #resolveTarget(raw) {
    return raw === 'done' ? 'done' : Number(raw);
  }

  /**
   * Reads `?step=` from location.search and returns a normalized target:
   * a 0-based deck index, or the literal string 'done'. Missing/blank,
   * non-numeric, or out-of-range values all fall back to 0 (step 1). If
   * the raw URL value didn't already unambiguously match the normalized
   * target, cleans the URL up via history.replaceState (no reload, no new
   * history entry).
   */
  #stepFromLocation() {
    const raw = new URLSearchParams(location.search).get('step');
    let target = 0;

    if (raw === 'done') {
      target = 'done';
    } else if (raw !== null && raw.trim() !== '') {
      const parsed = parseInt(raw, 10);
      const zeroBased = parsed - 1;
      if (Number.isFinite(parsed) && zeroBased >= 0 && zeroBased <= this.#deck.length - 1) {
        target = zeroBased;
      }
    }

    const isMissingOrBlank = raw === null || raw.trim() === '';
    const expectedRaw = target === 'done' ? 'done' : String(target + 1);
    if (!isMissingOrBlank && raw !== expectedRaw) {
      const url = new URL(location.href);
      url.searchParams.set('step', expectedRaw);
      history.replaceState(null, '', url);
    }

    return target;
  }

  /**
   * Shows the one matching giveaway panel (numeric target) and hides the
   * rest + the done panel, or — when target is the literal 'done' — hides
   * every giveaway panel and shows the done panel. Now only ever called
   * within a single page load — once on connect (from #stepFromLocation)
   * and once from #correctToCompleteOnLoad's in-place repaint — since every
   * other step change is a real navigation that lands on a fresh page
   * instead. Still the one place a pending auto-advance from
   * #showConfirmation()'s caller gets cancelled, every panel's content
   * gets repainted from the current summary, and one Sumo page_view fires
   * per step, regardless of what triggered the change.
   */
  #applyStep(target) {
    if (this.#confirmTimeout) {
      clearTimeout(this.#confirmTimeout);
      this.#confirmTimeout = null;
    }
    this.#renderAllPanels();

    const showGiveaway = target !== 'done';
    this.#deck.forEach((entry) => {
      entry.el.hidden = !(showGiveaway && entry.index === target);
    });

    if (this.#doneEl) {
      if (showGiveaway) {
        this.#doneEl.hidden = true;
      } else {
        this.#renderDonePanel();
        this.#doneEl.hidden = false;
      }
    }

    const shown = showGiveaway ? this.#deck[target] : null;
    this.#logEvent('page_view', {
      extra: {
        step: target,
        card_id: shown ? shown.cardId : null,
        giveaway_key: shown ? shown.key : null,
        round_current: this.#summary ? this.#summary.round_current : null,
        // Visitors now reach this page (no login wall) and have no
        // customer_id, so funnel queries filter on this instead.
        logged_in: this.dataset.loggedIn === 'true',
      },
    });
  }

  // A real page load, not a client-side repaint: the browser tears down
  // this page and requests the new `?step=` fresh, so the destination's own
  // connectedCallback re-derives the visible panel AND re-fetches the
  // summary from the server — never a stale in-memory copy. Nothing needs
  // pre-painting here since the current page is about to unload.
  #navigateTo(target) {
    const url = new URL(location.href);
    url.searchParams.set('step', target === 'done' ? 'done' : String(target + 1));
    location.href = url;
  }

  // The one silent exception to "navigation is a real page load": a plain
  // visit that turns out to already be fully complete for today corrects
  // itself in place (repaint + history.replaceState, no new page load) —
  // see the call site in #applySummary for why.
  #correctToCompleteOnLoad() {
    this.#applyStep('done');
    const url = new URL(location.href);
    url.searchParams.set('step', 'done');
    history.replaceState(null, '', url);
  }

  // Walks the deck in order and returns the index of the first giveaway
  // not yet entered this round, per the summary. Falls back to 0 if the
  // summary hasn't loaded yet, or if every giveaway already shows entered
  // (defensive — shouldn't happen outside a two-tab race; see the design
  // doc's error-handling contract).
  #firstUnenteredIndex() {
    if (!this.#summary) return 0;
    const index = this.#deck.findIndex((entry) => {
      const giveaway = this.#summaryByCard.get(entry.cardId);
      return giveaway && !giveaway.entered_this_round;
    });
    return index === -1 ? 0 : index;
  }

  // ---------- Context + summary (engagement Lambda) ----------

  /**
   * Fetches the signed context section, cache-busted so a refetch after a
   * 410 can't be served the same expired payload from HTTP cache — see
   * assets/watch-and-win-v5.js:826-835 for the production incident
   * (hundreds of thousands of false 410s) this guards against. Cached
   * in-memory for the page session; only replaced via forceRefresh, on a
   * 410. Mirrors loadWatchwinContext() in that same file almost exactly.
   */
  #loadContext(forceRefresh = false) {
    if (this.#shopContext && !forceRefresh) return Promise.resolve(this.#shopContext);

    return fetch(`/?sections=click-and-win-context&_=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Section API returned ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const html = data['click-and-win-context'];
        if (!html) throw new Error('No click-and-win-context in Section API response');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.getElementById('click-and-win-context-data');
        if (!el) throw new Error('No #click-and-win-context-data element in section HTML');
        // payload/signature are passed through untouched to the Lambda
        // everywhere below — never re-serialized. A separate, local-only
        // JSON.parse of the payload happens in #logEvent, purely to read
        // customer_id for Sumo; that parsed copy is never sent anywhere.
        this.#shopContext = { payload: el.dataset.payload, signature: el.dataset.signature };
        return this.#shopContext;
      });
  }

  #fetchSummary(ctx, isRetry) {
    const url = `${APP_PROXY_BASE}/clickandwin/summary?shop_context=${encodeURIComponent(JSON.stringify(ctx))}`;
    return fetch(url)
      .then((r) => {
        if (r.status === 410 && !isRetry) {
          return this.#loadContext(true).then((ctx2) => this.#fetchSummary(ctx2, true));
        }
        if (r.status === 401) {
          // No client-side login wall to swap to — the gate is server-side.
          // Reloading gets a fresh render, which shows the login wall for a
          // session that's gone stale. Mirrors #handleEntryResponse's 401 case.
          location.reload();
          return;
        }
        return r.json().catch(() => ({})).then((data) => {
          if (!r.ok) {
            this.#logEvent('summary_failed', { error_type: `http_${r.status}` });
            this.#disableAllEnterButtons(GENERIC_ERROR_LABEL);
            return;
          }
          this.#applySummary(data);
        });
      })
      .catch((err) => {
        this.#logEvent('summary_failed', { error_type: 'network', extra: { message: err && err.message } });
        this.#disableAllEnterButtons(GENERIC_ERROR_LABEL);
      });
  }

  // Non-200, non-410 summary failure (or a context fetch that never
  // resolved at all): every Enter button gets disabled with a generic
  // retry-oriented label — never a fallback to mocked counts. Retrying
  // means reloading the page (which re-runs connectedCallback), not a
  // dedicated in-page retry control — no such UI exists today and adding
  // one is out of scope for this pass, same call as the deferred "+N
  // entries" copy.
  // No-session counterpart of #renderAllPanels: there is no summary and
  // never will be, so the only summary-derived content a visitor would
  // otherwise see blank — the "ROUND {1 OF 3}" eyebrow — is painted with
  // round 1, the only round a visitor can be in. Button state and stepper
  // checkmarks are left as Liquid rendered them (no Enter button exists on
  // this path, and position-based stepper styling is right for browsing).
  #renderNoSessionPanels() {
    const eyebrowTemplate = this.dataset.roundEyebrowLabel || '';
    this.#deck.forEach((entry) => {
      const eyebrow = entry.el.querySelector('.caw__giveaway-eyebrow');
      if (eyebrow) renderEyebrowText(eyebrow, eyebrowTemplate, 1);
    });
  }

  #disableAllEnterButtons(labelText) {
    this.#deck.forEach((entry) => {
      const button = entry.el.querySelector('.caw__btn--enter');
      if (!button) return;
      button.dataset.state = 'unconfigured';
      button.disabled = true;
      const spinner = button.querySelector('.caw__btn-spinner');
      if (spinner) spinner.hidden = true;
      const label = button.querySelector('.caw__btn-label');
      if (label) label.textContent = labelText;
    });
  }

  /**
   * THE one place round state is decided. The contract specifies it as a pure
   * function of per-card entry counts (CLIENT_DEV_GUIDE.md → "Click & Win"):
   * there is no round counter anywhere, the round is derived from the counts.
   *
   *   round_current      = min(entries_today) + 1, capped at rounds_per_day
   *   entered_this_round = entries_today >= round_current
   *   complete           = every entries_today >= rounds_per_day
   *
   * The summary sends its own round_current/complete/entered_this_round and an
   * entry response sends round_completed; all four are treated as ADVISORY and
   * recomputed here. Keeping a mutable mirror of a derived value is what let a
   * single entry response's opinion walk the flow into a state its own counts
   * could not produce — see tasks/todo.md for the trace.
   *
   * Derived over CONFIGURED cards only: a card absent from summary.giveaways[]
   * (it appears in unconfigured_cards) can never be entered, so counting
   * it as 0 would pin min() at 0 forever and freeze the flow at round 1 with
   * nothing enterable. Same call #doneRoundView already documents for
   * enteredInRound. Nothing here assumes a deck size or a round count — the deck
   * is whatever Liquid rendered, rounds_per_day is whatever the server said.
   */
  #deriveRoundState() {
    if (!this.#summary) return;
    const roundsPerDay = Number(this.#summary.rounds_per_day) || 0;

    const counts = [];
    this.#deck.forEach((entry) => {
      const giveaway = this.#summaryByCard.get(entry.cardId);
      if (giveaway) counts.push(Number(giveaway.entries_today) || 0);
    });

    const minEntries = counts.length ? Math.min(...counts) : 0;
    const roundCurrent = roundsPerDay > 0
      ? Math.min(minEntries + 1, roundsPerDay)
      : minEntries + 1;

    this.#summary.round_current = roundCurrent;
    this.#summary.complete = counts.length > 0 && roundsPerDay > 0 && minEntries >= roundsPerDay;

    this.#summaryByCard.forEach((giveaway) => {
      const entriesToday = Number(giveaway.entries_today) || 0;
      giveaway.entered_this_round = entriesToday >= roundCurrent;
      // Only ever set, never cleared: #handleEntryError's DAILY_CAP_REACHED
      // branch sets this from a response whose count we may not have, and a
      // later derive must not silently un-cap that giveaway.
      if (roundsPerDay > 0 && entriesToday >= roundsPerDay) giveaway.dailyCapReached = true;
    });
  }

  #applySummary(summary) {
    this.#summary = summary;
    this.#summaryByCard = new Map((summary.giveaways || []).map((g) => [String(g.card_id), g]));
    this.#deriveRoundState();
    this.#renderAllPanels();
    this.#renderDonePanel();

    // A plain visit (no ?step= at all) that turns out to already be fully
    // complete for today shouldn't land on a giveaway panel whose Enter
    // button can only ever read "complete" — send it straight to the
    // done/complete panel instead. Only applies to that default landing; an
    // explicit ?step= (a direct link, or Prev/Next inside the deck) is
    // always honored even once the flow is complete — Prev/Next are plain
    // links, always enabled regardless of entry state (see
    // snippets/click-and-win-prize-card.liquid).
    if (this.#landedOnDefaultStep && this.#summary.complete) {
      this.#correctToCompleteOnLoad();
    }
  }

  // ---------- Enter flow ----------

  #postEntry(button) {
    const giveawayEl = button.closest('.caw__giveaway');
    if (!giveawayEl) return;

    button.dataset.state = 'pending';
    button.disabled = true;
    const spinner = button.querySelector('.caw__btn-spinner');
    if (spinner) spinner.hidden = false;
    const label = button.querySelector('.caw__btn-label');
    if (label) label.textContent = button.dataset.labelPending;

    const cardId = giveawayEl.dataset.cardId;
    const nextTarget = this.#resolveTarget(giveawayEl.dataset.nextTarget);
    const submitStartedAt = Date.now();
    this.#pendingCardId = cardId;
    this.#logEvent('entry_submitted', { extra: this.#entryExtra(giveawayEl) });

    this.#loadContext()
      .then((ctx) => this.#submitEntry(ctx, cardId, false))
      .then((result) => this.#handleEntryResponse(result, giveawayEl, button, nextTarget, submitStartedAt))
      .catch((err) => this.#handleEntryError(giveawayEl, button, 'network', submitStartedAt, err))
      .then(() => { this.#pendingCardId = null; });
  }

  // Sumo reports both: card_id is the panel the member acted on, giveaway_key
  // the drawing it feeds (reporting keys on the drawing, and keys repeat).
  #entryExtra(giveawayEl) {
    return {
      card_id: giveawayEl.dataset.cardId,
      giveaway_key: giveawayEl.dataset.giveawayKey,
    };
  }

  // POST /apps/engagement/entries with shop_context; refetch context +
  // retry once on 410 (expired context) — mirrors doSubmitEntry() in
  // assets/watch-and-win-v5.js:1090-1101. No source_ref: optional and
  // ignored per the backend contract (the ledger builds its own), so it's
  // simply omitted rather than client-generated. Only card_id is sent — the
  // Lambda looks the giveaway key up from the signed deck and rejects a
  // client-supplied one.
  #submitEntry(ctx, cardId, isRetry) {
    return fetch(`${APP_PROXY_BASE}/entries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flow: 'click_and_win', card_id: cardId, shop_context: ctx }),
    }).then((r) => {
      if (r.status === 410 && !isRetry) {
        return this.#loadContext(true).then((ctx2) => this.#submitEntry(ctx2, cardId, true));
      }
      return r.json().catch(() => ({})).then((data) => ({ status: r.status, data }));
    });
  }

  // Folds one Enter response into the model: the ONLY thing stored is this
  // giveaway's own entry count, then every round-level value is re-derived
  // from the counts (#deriveRoundState). The model otherwise never changes
  // after page load and #renderAllPanels/#renderDonePanel always repaint from
  // it, so without this an entry visibly "un-happens" on the next navigation.
  //
  // `round_completed` is deliberately NOT read. It is the server's claim that
  // this click finished a round; the counts already say whether it did, and
  // when the two disagreed it was the claim that was wrong.
  #applyEntryToModel(cardId, data) {
    const giveaway = this.#summaryByCard.get(cardId);
    if (!giveaway) return;

    if (data.entries_today_for_giveaway != null) {
      giveaway.entries_today = Number(data.entries_today_for_giveaway);
    } else if (data.success && !data.duplicate) {
      // Fallback for a 200 that omitted the count (it is in the contract, so
      // this should not happen). A duplicate or a 422 recorded no new entry,
      // so neither one moves the count.
      giveaway.entries_today = (Number(giveaway.entries_today) || 0) + 1;
    }

    this.#deriveRoundState();
  }

  // One branch per row of the design doc's error-handling contract table.
  // 200/duplicate:false, 200/duplicate:true, and 422 ROUND_NOT_COMPLETE
  // all produce the identical UI action (mark entered, show the
  // confirmation overlay, auto-advance) — kept as separate explicit
  // branches, not merged into one condition, so each logs its own
  // `entry_result` distinctly and the mapping to the doc's table stays
  // obvious at a glance.
  //
  // WHERE AN ENTER GOES NEXT: this panel's own `nextTarget` (`data-next-target`,
  // the literal 'done' only on the deck's last panel) — UNLESS this Enter is
  // the one that finished the round, or the day, in which case it goes to the
  // done panel from wherever it was made. #afterEntryTarget decides, from the
  // model's counts before vs. after the response is folded in. Without that
  // exception a member who entered the deck out of order (skipped ahead with
  // the arrows, came back via "Finish Round", entered the last one they owed
  // mid-deck) advanced straight onto the next card, which by then reported
  // the NEXT round — the "Round N complete" summary, its ledger and its
  // "Go To Next Round" button were never shown. It only looked right in
  // strict order because the last card owed is then also the deck's last
  // card, whose nextTarget is 'done' anyway.
  //
  // Still NOT routed on: the response body's `round_completed`. Earlier
  // passes keyed on it (and on a mirrored `complete` flag) and both fired on
  // members with giveaways left; a deck the server had silently shrunk to one
  // configured card made every entry "complete" a round. The counts are the
  // same source of truth every button state is painted from, and they can
  // only say a round finished when every configured card's count rose.
  //
  // The destination is computed ONCE per Enter and stamped on the panel as
  // data-after-entry-target, so the auto-advance and the relabeled "Next"
  // button (#onClick's 'confirmed' branch) cannot disagree.
  #handleEntryResponse({ status, data }, giveawayEl, button, nextTarget, submitStartedAt) {
    const latency_ms = Date.now() - submitStartedAt;
    const cardId = giveawayEl.dataset.cardId;
    const extra = Object.assign(this.#entryExtra(giveawayEl), {
      duplicate: !!data.duplicate,
      round: data.round,
      round_completed: data.round_completed,
    });
    const before = this.#roundSnapshot();

    if (status === 200 && !data.duplicate) {
      this.#logEvent('entry_result', { result: 'success', latency_ms, extra });
      this.#applyEntryToModel(cardId, data);
      if (this.#summary) this.#summary.total_entries_today += (data.entries_earned || 0);
      if (giveawayEl.hidden) return;
      this.#markEntered(button);
      this.#showConfirmation(giveawayEl, button);
      this.#scheduleAutoAdvance(this.#afterEntryTarget(before, nextTarget, giveawayEl));
      return;
    }

    if (status === 200 && data.duplicate) {
      this.#logEvent('entry_result', { result: 'duplicate', latency_ms, extra });
      this.#applyEntryToModel(cardId, data);
      if (giveawayEl.hidden) return;
      this.#markEntered(button);
      this.#showConfirmation(giveawayEl, button);
      this.#scheduleAutoAdvance(this.#afterEntryTarget(before, nextTarget, giveawayEl));
      return;
    }

    if (status === 422 && data.reason === 'ROUND_NOT_COMPLETE') {
      this.#logEvent('entry_result', { result: 'duplicate', error_type: 'ROUND_NOT_COMPLETE', latency_ms, extra });
      this.#applyEntryToModel(cardId, data);
      if (giveawayEl.hidden) return;
      this.#markEntered(button);
      this.#showConfirmation(giveawayEl, button);
      this.#scheduleAutoAdvance(this.#afterEntryTarget(before, nextTarget, giveawayEl));
      return;
    }

    if (status === 422 && data.reason === 'DAILY_CAP_REACHED') {
      this.#logEvent('entry_result', { result: 'error', error_type: 'DAILY_CAP_REACHED', latency_ms, extra });
      const giveaway = this.#summaryByCard.get(cardId);
      if (giveaway) giveaway.dailyCapReached = true;
      if (giveawayEl.hidden) return;
      button.dataset.state = 'complete';
      button.disabled = true;
      const spinnerEl = button.querySelector('.caw__btn-spinner');
      if (spinnerEl) spinnerEl.hidden = true;
      const labelEl = button.querySelector('.caw__btn-label');
      if (labelEl) labelEl.textContent = button.dataset.labelComplete;
      // A cap refusal recorded nothing, so no round can have finished here —
      // but if the day is already complete the done panel is still the
      // right place, not a card whose only button reads "complete".
      this.#navigateTo(this.#afterEntryTarget(before, nextTarget, giveawayEl));
      return;
    }

    if (status === 401) {
      this.#logEvent('entry_result', { result: 'error', error_type: 'unauthorized', latency_ms, extra });
      // No client-side login wall to swap to — the gate is server-side
      // (sections/click-and-win.liquid's `{% if customer %}`). Reloading
      // gets a fresh server render, which shows the login wall for a
      // session that's gone stale.
      location.reload();
      return;
    }

    this.#handleEntryError(giveawayEl, button, `http_${status}`, submitStartedAt);
  }

  // The two round-level facts an Enter can change, read BEFORE the response
  // is folded into the model so #afterEntryTarget can see whether it did.
  // Both come from #deriveRoundState, i.e. from the counts, never from a
  // response body.
  #roundSnapshot() {
    if (!this.#summary) return { roundCurrent: 0, complete: false };
    return {
      roundCurrent: Number(this.#summary.round_current) || 0,
      complete: !!this.#summary.complete,
    };
  }

  /**
   * Where the member goes after THIS Enter: the done panel if the Enter
   * finished the round (round_current rose — it only can when every
   * configured card's count is now at the new minimum) or finished the day
   * (complete flipped on), otherwise the panel's own nextTarget. Stamped on
   * the panel so the auto-advance and the relabeled "Next" button read the
   * same answer. With no summary loaded there is nothing to compare, so it
   * degrades to the plain one-step rule.
   */
  #afterEntryTarget(before, nextTarget, giveawayEl) {
    const after = this.#roundSnapshot();
    const finishedRound = this.#summary && (
      (after.complete && !before.complete) ||
      after.roundCurrent > before.roundCurrent
    );
    const target = finishedRound ? 'done' : nextTarget;
    if (giveawayEl) giveawayEl.dataset.afterEntryTarget = target === 'done' ? 'done' : String(target);
    return target;
  }

  #handleEntryError(giveawayEl, button, errorType, submitStartedAt, err) {
    const latency_ms = Date.now() - submitStartedAt;
    this.#logEvent('entry_result', {
      result: 'error',
      error_type: errorType,
      latency_ms,
      extra: Object.assign(this.#entryExtra(giveawayEl), { message: err && err.message }),
    });

    // Re-enable Enter — the button itself is the retry affordance. No
    // navigation, no "+N" toast, per the design doc's contract.
    button.dataset.state = 'available';
    button.disabled = false;
    const spinner = button.querySelector('.caw__btn-spinner');
    if (spinner) spinner.hidden = true;
    const label = button.querySelector('.caw__btn-label');
    if (label) label.textContent = GENERIC_ERROR_LABEL;
  }

  #markEntered(button) {
    button.dataset.state = 'entered';
    button.disabled = true;
    const spinner = button.querySelector('.caw__btn-spinner');
    if (spinner) spinner.hidden = true;
    const label = button.querySelector('.caw__btn-label');
    if (label) label.textContent = button.dataset.labelEntered;
  }

  // Switches on the transient, JS-only "confirmed" overlay (Figma node
  // 7065:6374) on top of the persisted state contract — relabels the
  // Enter button "Next" and re-enables it (a click on it advances
  // immediately; see #onClick's 'enter' case), and reveals the
  // prize-media overlay.
  #showConfirmation(giveawayEl, button) {
    button.dataset.state = 'confirmed';
    button.disabled = false;
    const label = button.querySelector('.caw__btn-label');
    if (label) label.textContent = button.dataset.labelConfirmed;

    const overlay = giveawayEl.querySelector('.caw__prize-overlay');
    if (overlay) overlay.hidden = false;

    // Focus was lost to <body> the moment #postEntry disabled this button —
    // the HTML spec blurs an element that becomes disabled — so until now a
    // keyboard Enter after entering did nothing at all, and the aria-live
    // confirmation above was announced from nowhere in particular. Handing
    // focus back makes the relabeled "Next" the member's current control, so
    // a keyboard Enter reaches #onClick's 'confirmed' branch and advances
    // exactly like clicking it, rather than depending on whether the browser
    // happened to focus the button on mousedown (Chrome does, Safari and
    // Firefox don't). preventScroll because the button sits below the prize
    // image the confirmation overlay just appeared on: the member should keep
    // reading that, not be yanked down to the control.
    if (typeof button.focus === 'function') button.focus({ preventScroll: true });
  }

  #scheduleAutoAdvance(target) {
    this.#confirmTimeout = setTimeout(() => {
      this.#confirmTimeout = null;
      this.#navigateTo(target);
    }, ENTER_CONFIRM_DURATION_MS);
  }

  // ---------- Rendering ----------

  #renderAllPanels() {
    if (!this.#summary) return;

    // Computed once and shared across every panel this pass — NOT once
    // per panel, so a single render can't observe two different "current
    // round" values for the same instant.
    const roundCurrent = this.#summary.round_current;
    const eyebrowTemplate = this.dataset.roundEyebrowLabel || '';

    this.#deck.forEach((entry) => {
      const giveaway = this.#summaryByCard.get(entry.cardId);
      const hasPendingEntry = this.#pendingCardId === entry.cardId;

      // Most-restrictive first. A card missing from the summary
      // (absent from `giveaways[]`, listed in `unconfigured_cards`)
      // is 'unconfigured' regardless of the flow's overall complete state.
      let state;
      if (!giveaway) {
        state = 'unconfigured';
      } else if (giveaway.dailyCapReached || this.#summary.complete) {
        state = 'complete';
      } else if (giveaway.entered_this_round) {
        state = 'entered';
      } else {
        state = 'available';
      }

      const button = entry.el.querySelector('.caw__btn--enter');
      if (button && !hasPendingEntry) {
        button.dataset.state = state;
        const label = button.querySelector('.caw__btn-label');
        if (label) label.textContent = button.dataset[STATE_LABEL_KEYS[state]];
        button.disabled = state !== 'available';
        const spinner = button.querySelector('.caw__btn-spinner');
        if (spinner) spinner.hidden = true;
      }

      // Swap this giveaway's own step-circle between its numeral and a
      // "✓", and paint it orange (.caw__step.is-done) on the same
      // condition — driven by the same button state above. Liquid renders
      // is-done from PAGE POSITION (every step before the current panel),
      // which agrees with entry state only when the member goes in order;
      // out of order it showed a check in a beige circle, or turned an
      // entered step beige on Prev. Overriding it here makes orange mean
      // "entered" on the deck stepper exactly as it already does on the
      // done panel's recap stepper (#syncDoneStepper). is-current and the
      // track fill are left alone — they still show position.
      // 'unconfigured' also shows a checkmark: a deliberate simplification
      // (it's a rare, temporary merchant-side misconfiguration, not a
      // normal customer-facing state, so a third distinct stepper glyph
      // isn't worth the extra markup/CSS).
      const isEntered = state !== 'available';
      this.querySelectorAll(`.caw__giveaways [data-step-index="${entry.index}"]`).forEach((circleEl) => {
        circleEl.textContent = isEntered ? '✓' : String(entry.index + 1);
        const stepEl = circleEl.closest('.caw__step');
        if (stepEl) stepEl.classList.toggle('is-done', isEntered);
      });

      const overlay = entry.el.querySelector('.caw__prize-overlay');
      if (overlay) overlay.hidden = true;

      const eyebrow = entry.el.querySelector('.caw__giveaway-eyebrow');
      if (eyebrow) renderEyebrowText(eyebrow, eyebrowTemplate, roundCurrent);
    });
  }

  /**
   * WHICH ROUND THE DONE PANEL IS TALKING ABOUT, and how far into it the
   * member actually is. Prev/Next are always enabled and `?step=done` is a
   * plain URL, so this panel is routinely reached with PARTIAL round
   * progress — it must never assume the round it shows is finished.
   *
   * `enteredInRound` is the count of giveaways reporting `entered_this_round`,
   * and it is the whole test, because "every giveaway entered this round" is
   * never observable: the server derives round_current = min(entries_today) + 1,
   * so the instant the last one is entered, min rises and the summary reports
   * the NEXT round with everything un-entered again. Therefore:
   *   enteredInRound > 0   → mid-round; the round in progress (round_current)
   *                          is the one to show, and it is NOT finished.
   *   enteredInRound === 0 → either nothing has started (round_current === 1)
   *                          or a round just completed; show round_current - 1,
   *                          floored at 1 so nothing ever reads "round 0".
   * Cards absent from summary.giveaways[] (an unconfigured key, listed in
   * unconfigured_cards) simply don't count toward `enteredInRound`:
   * they can never be entered, so they can neither start a round nor hold one
   * open. Deliberately NOT also compared against a count of configured
   * giveaways — "enteredInRound is short of the full deck" is implied by
   * enteredInRound > 0 for exactly the reason above, so the extra conjunct
   * could only ever change the answer in a state the contract says is
   * impossible, and there it changed it the wrong way: it fell through to the
   * "nothing started" branch and told a member who had already entered to
   * "start your first round".
   */
  #doneRoundView() {
    const roundCurrent = this.#summary.round_current;
    let enteredInRound = 0;

    this.#deck.forEach((entry) => {
      const giveaway = this.#summaryByCard.get(entry.cardId);
      if (giveaway && giveaway.entered_this_round) enteredInRound += 1;
    });

    return {
      roundCurrent,
      enteredInRound,
      roundShown: enteredInRound > 0 ? roundCurrent : Math.max(roundCurrent - 1, 1),
      // The guard the heading and the CTA both branch on: the round this
      // panel is showing still has giveaways the member hasn't entered, so it
      // must not present itself as a finished round with a next one waiting.
      roundUnfinished: enteredInRound > 0,
    };
  }

  #renderDonePanel() {
    if (!this.#summary || !this.#doneEl) return;

    const incompleteEl = this.#doneEl.querySelector('.caw__done-incomplete');
    const completeEl = this.#doneEl.querySelector('.caw__done-complete');
    const eyebrowTemplate = this.dataset.roundEyebrowLabel || '';
    const doneEyebrow = this.#doneEl.querySelector('.caw__giveaway-eyebrow');
    const view = this.#doneRoundView();

    if (this.#summary.complete) {
      if (completeEl) completeEl.hidden = false;
      if (incompleteEl) incompleteEl.hidden = true;
      if (doneEyebrow) renderEyebrowText(doneEyebrow, eyebrowTemplate, view.roundCurrent);

      if (completeEl) {
        // snippets/click-and-win-ledger.liquid renders a fixed THREE round
        // tables — Liquid can't read rounds_per_day, which only exists in the
        // summary. Each carries its own data-round, which does two things
        // here: a table past the real cap is hidden outright (a 2-round
        // configuration must not show a phantom "ROUND 3"), and the rest each
        // threshold on the round they are labeled with instead of sharing one
        // value. On a genuinely complete flow every giveaway is at the cap, so
        // the surviving tables do all agree — the per-table threshold is what
        // keeps that a consequence of the data rather than an assumption.
        const roundsPerDay = this.#summary.rounds_per_day || Infinity;
        completeEl.querySelectorAll('.caw__done-ledger').forEach((tableEl) => {
          const round = Number(tableEl.dataset.round);
          const wrapper = tableEl.closest('.caw__done-ledger-round');
          if (round && round > roundsPerDay) {
            if (wrapper) wrapper.hidden = true;
            return;
          }
          if (wrapper) wrapper.hidden = false;
          this.#fillLedgerTable(tableEl, view.roundShown);
        });
        this.#trimCompleteStepper(completeEl, roundsPerDay);
        const grandTotalEl = completeEl.querySelector('[data-role="done-grand-total"]');
        if (grandTotalEl) grandTotalEl.textContent = formatEntries(this.#summary.total_entries_today);
      }
      return;
    }

    if (incompleteEl) incompleteEl.hidden = false;
    if (completeEl) completeEl.hidden = true;
    if (doneEyebrow) renderEyebrowText(doneEyebrow, eyebrowTemplate, view.roundShown);

    // Three-way, most-specific first. The old two-way branched on
    // round_current alone, which told a member sitting mid-round N+1 that
    // they were "entered into Round N, ready for another round", and told one
    // who had already entered part of round 1 to "start your first round".
    const headingEl = this.#doneEl.querySelector('[data-role="done-heading"]');
    if (headingEl) {
      let template;
      if (view.roundUnfinished) {
        template = this.dataset.donePartialHeadingLabel || '';
      } else if (view.roundCurrent === 1) {
        template = this.dataset.doneHeadingNoneLabel || '';
      } else {
        template = this.dataset.doneHeadingLabel || '';
      }
      headingEl.textContent = template.replace('[[ round ]]', String(view.roundShown));
    }

    const ledgerEl = incompleteEl?.querySelector('.caw__done-ledger');
    if (ledgerEl) this.#fillLedgerTable(ledgerEl, view.roundShown);
    this.#syncDoneStepper(incompleteEl, view.roundShown);

    // The CTA navigates to #firstUnenteredIndex() either way (see #onClick's
    // 'start-next-round' case) — what changes is the promise it makes. While
    // the round in progress still has un-entered giveaways there is no next
    // round to start (the server won't derive one until this one is done), so
    // it offers to finish THIS round instead and the member lands on the
    // giveaway they still owe, rather than on a button that lies to them.
    const startNextEl = this.#doneEl.querySelector('[data-role="done-start-next-cta"]');
    if (startNextEl) {
      const template = view.roundUnfinished
        ? (this.dataset.doneFinishRoundCtaLabel || '')
        : (this.dataset.doneStartNextCtaLabel || '');
      const ctaRound = view.roundUnfinished ? view.roundShown : view.roundCurrent;
      startNextEl.textContent = template.replace('[[ round ]]', String(ctaRound));
    }
  }

  /**
   * Hides the surplus nodes of the all-rounds-complete panel's compact
   * "ROUND 1/2/3" cluster, for the same reason its ledger tables are hidden
   * above: snippets/click-and-win-ledger.liquid renders a fixed three because
   * Liquid can't read rounds_per_day. Without this the panel contradicts
   * itself under a 2-round configuration — two ledger tables under three
   * all-checked circles. Each node's connector segment precedes it in DOM
   * order (see that snippet's compact branch), so it is hidden alongside.
   *
   * Only ever trims: a cap ABOVE three still renders three, since Liquid
   * emitted no fourth node to reveal. That direction needs the snippet to
   * loop rounds_per_day, which it cannot; the "of 3" eyebrow and "all 3
   * rounds" heading are merchant copy with the same limit.
   */
  #trimCompleteStepper(completeEl, roundsPerDay) {
    if (!Number.isFinite(roundsPerDay)) return;
    completeEl.querySelectorAll('.caw__stepper-track--compact .caw__step').forEach((stepEl) => {
      const circle = stepEl.querySelector('[data-step-index]');
      if (!circle) return;
      const surplus = Number(circle.dataset.stepIndex) >= roundsPerDay;
      stepEl.hidden = surplus;
      const connector = stepEl.previousElementSibling;
      if (connector && connector.classList.contains('caw__step-connector')) connector.hidden = surplus;
    });
  }

  /**
   * Marks the done panel's deck stepper from what the member has actually
   * entered. snippets/click-and-win-ledger.liquid renders it with
   * current_index: -1 — nothing pre-checked — because Liquid can't know any of
   * this; it used to pass `deck_count` (one past the last index), which
   * server-rendered EVERY circle is-done + "✓" with the fill bar at 100%, so
   * opening ?step=done having entered nothing still showed a fully completed
   * round. #renderAllPanels()'s own circle pass can't correct it: that one is
   * deliberately scoped to `.caw__giveaways`, where a circle means deck
   * POSITION, not entry state.
   *
   * Uses the same predicate as #fillLedgerTable, so a circle is never checked
   * above a row reading 0. The fill bar spans to the LAST entered step rather
   * than to a count of them: entered giveaways can be non-contiguous (the Next
   * arrow lets a member skip one without entering), and a proportional
   * "3 of 5 done" fill would point at the wrong three.
   */
  #syncDoneStepper(incompleteEl, roundShown) {
    const track = incompleteEl && incompleteEl.querySelector('.caw__stepper-track');
    if (!track) return;

    let lastEnteredIndex = -1;
    this.#deck.forEach((entry) => {
      const giveaway = this.#summaryByCard.get(entry.cardId);
      const isEntered = !!giveaway && giveaway.entries_today >= roundShown;
      const circle = track.querySelector(`[data-step-index="${entry.index}"]`);
      if (!circle) return;
      circle.textContent = isEntered ? '✓' : String(entry.index + 1);
      const step = circle.closest('.caw__step');
      if (step) step.classList.toggle('is-done', isEntered);
      if (isEntered) lastEnteredIndex = entry.index;
    });

    const fill = track.querySelector('.caw__stepper-track-fill');
    if (fill) {
      const lastStepIndex = this.#deck.length - 1;
      const fraction = lastStepIndex > 0 && lastEnteredIndex > 0 ? lastEnteredIndex / lastStepIndex : 0;
      fill.style.width = `calc((100% - var(--caw-step-w)) * ${fraction})`;
    }
  }

  // Fills one ledger table's per-row entries values and its own total
  // badge from the summary, matched by card_id (never by array
  // position — summary.giveaways[] can omit an unconfigured card entirely,
  // which would otherwise shift every later row). Row order/labels stay
  // exactly as server-rendered (deck order, from deck_titles) — only the
  // per-row VALUE lookup is by card id.
  //
  // A table may name the round it summarizes itself via data-round (the
  // all-rounds-complete panel's three ROUND 1/2/3 tables do); `roundShown` is
  // the fallback for the single table that doesn't carry one.
  #fillLedgerTable(tableEl, roundShown) {
    const round = Number(tableEl.dataset.round) || roundShown;
    let total = 0;
    tableEl.querySelectorAll('[data-role="done-ledger-entries"]').forEach((entryEl, i) => {
      const entry = this.#deck[i];
      const giveaway = entry && this.#summaryByCard.get(entry.cardId);
      const value = giveaway && giveaway.entries_today >= round ? giveaway.entries_per_enter : 0;
      entryEl.textContent = formatEntries(value);
      total += value;
    });
    const totalEl = tableEl.querySelector('[data-role="done-ledger-total"]');
    if (totalEl) totalEl.textContent = formatEntries(total);
  }

  // ---------- Sumo (assets/sumo-client.js) ----------

  // Thin wrapper mirroring assets/watch-and-win-v5.js:126-153's logEvent —
  // module is a flat string ('clickandwin', matching that file's
  // 'watchwin'), and every field not in sumo-client.js's actual whitelist
  // goes under `extra` (W&W's own wrapper puts a few non-whitelisted
  // fields at the top level, where they're silently dropped — not
  // repeated here).
  #logEvent(eventName, fields) {
    if (!window.logClientEvent) return;
    fields = fields || {};

    let customerId = null;
    if (this.#shopContext) {
      try {
        customerId = JSON.parse(this.#shopContext.payload).customer_id || null;
      } catch (_error) {
        customerId = null;
      }
    }

    const base = {
      event_name: eventName,
      module: 'clickandwin',
      shopify_customer_id: customerId,
    };

    const merged = Object.assign(base, fields);
    merged.extra = fields.extra || {};
    window.logClientEvent(merged);
  }
}

customElements.define('click-and-win-flow', ClickAndWinFlow);
